import { downloadBlobLocally } from './downloadBlobLocally.js';
import { shareBlobFiles } from './shareBlobFiles.js';
import { FullPageExportShareAbortedError } from './types.js';
import type { FullPageExportDelivery, ShareOrDownloadOptions } from './types.js';

/** Keep the blob URL alive long enough for the browser to start the download. */
const DOWNLOAD_BLOB_REVOKE_MS = 1000;

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err instanceof Error && err.name === 'AbortError')
  );
}

/**
 * Deliver an export blob.
 *
 * - `download` — local save only (never `navigator.share`)
 * - `share` — Web Share only (no download fallback; abort → {@link FullPageExportShareAbortedError})
 * - omitted / legacy callers — prefer share when available, else download (auto)
 */
export async function shareOrDownloadBlob(
  blob: Blob,
  options: ShareOrDownloadOptions,
  delivery?: FullPageExportDelivery,
): Promise<void> {
  if (delivery === 'download') {
    downloadBlobLocally(blob, options);
    return;
  }

  if (delivery === 'share') {
    await shareBlobFiles(blob, options);
    return;
  }

  const url = URL.createObjectURL(blob);
  let startedDownload = false;
  try {
    const shareFile = new File([blob], options.filename, { type: options.mimeType });
    const canShareFiles =
      typeof navigator.share === 'function' &&
      typeof navigator.canShare === 'function' &&
      navigator.canShare({ files: [shareFile] });

    if (canShareFiles) {
      try {
        await navigator.share({
          files: [shareFile],
          title: options.filename,
        });
        return;
      } catch (err) {
        if (isAbortError(err)) {
          throw new FullPageExportShareAbortedError(err);
        }
      }
    }

    const dl = document.createElement('a');
    dl.href = url;
    dl.download = options.filename;
    dl.click();
    startedDownload = true;
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, DOWNLOAD_BLOB_REVOKE_MS);
  } finally {
    if (!startedDownload) {
      URL.revokeObjectURL(url);
    }
  }
}
